import { PermissionCode, RoleCode, passwordSchema, type UserDetail } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';

import { USERS_ADMIN_PATH } from '@/platform/lib/admin';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import {
  departmentSelectOptions,
  teamSelectOptions,
} from '@/platform/features/users/org-select-options';
import {
  useAssignUserRoles,
  useSendLocalPasswordLink,
  useSetLocalPassword,
  useUpdateUser,
  useUser,
  useUserLookups,
  useUsers,
} from '@/platform/features/users/use-users';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { USER_JOB_TITLE_LABEL } from '@/platform/features/users/user-labels';
import { PersonChip } from '@/platform/components/person-chip';

type FormState = {
  firstName: string;
  lastName: string;
  phone: string;
  personnelNumber: string;
  departmentId: string;
  teamId: string;
  managerId: string;
  managerLocked: boolean;
  roles: string[];
};

function toForm(user: UserDetail): FormState {
  return {
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phone ?? '',
    personnelNumber: user.personnelNumber ?? '',
    departmentId: user.department?.id ?? 'none',
    teamId: user.team?.id ?? 'none',
    managerId: user.managerId ?? 'none',
    managerLocked: user.managerLocked,
    roles: user.roleAssignments.map((role) => role.code),
  };
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const userQuery = useUser(userId);

  if (userQuery.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Benutzer wird geladen …
      </p>
    );
  }

  if (userQuery.isError || !userQuery.data) {
    return (
      <section className="space-y-3">
        <h1 className="text-2xl font-bold">Benutzer nicht gefunden</h1>
        <p className="text-muted-foreground">
          {userQuery.error instanceof ApiRequestError
            ? userQuery.error.message
            : 'Der Datensatz konnte nicht geladen werden.'}
        </p>
        <Button asChild variant="outline">
          <Link to={USERS_ADMIN_PATH}>Zurück zur Liste</Link>
        </Button>
      </section>
    );
  }

  return <UserDetailForm user={userQuery.data} />;
}

function UserDetailForm({ user }: { user: UserDetail }) {
  const copy = useVoiceCopy();
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.USERS_MANAGE);
  const canAssignRoles = hasPermission(me.data, PermissionCode.USERS_ASSIGN_ROLE);
  const canAssignOperator = hasPermission(me.data, PermissionCode.TENANTS_MANAGE);
  const lookups = useUserLookups();
  const people = useUsers({
    page: 1,
    pageSize: 200,
    sortBy: 'displayName',
    sortOrder: 'asc',
    status: 'active',
  });
  const updateUser = useUpdateUser(user.id);
  const assignRoles = useAssignUserRoles(user.id);
  const [form, setForm] = useState<FormState>(() => toForm(user));
  const isSelf = me.data?.id === user.id;

  const departmentOptions = departmentSelectOptions(lookups.data?.departments, user.department);
  const teamOptions = teamSelectOptions(lookups.data?.teams, user.team);

  async function onSave(event: FormEvent) {
    event.preventDefault();
    try {
      await updateUser.mutateAsync({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        displayName: `${form.firstName.trim()} ${form.lastName.trim()}`,
        phone: form.phone.trim() === '' ? null : form.phone.trim(),
        personnelNumber: form.personnelNumber.trim() === '' ? null : form.personnelNumber.trim(),
        departmentId: form.departmentId === 'none' ? null : form.departmentId,
        teamId: form.teamId === 'none' ? null : form.teamId,
        managerId: form.managerId === 'none' ? null : form.managerId,
        managerLocked: form.managerLocked,
      });
      if (canAssignRoles) {
        await assignRoles.mutateAsync({ roles: form.roles });
      }
      toast.success('Änderungen gespeichert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function onSetActive(isActive: boolean) {
    try {
      await updateUser.mutateAsync({ isActive });
      toast.success(isActive ? 'Benutzer wurde reaktiviert.' : 'Benutzer wurde deaktiviert.');
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Aktion fehlgeschlagen.');
    }
  }

  function toggleRole(code: string, checked: boolean) {
    setForm((current) => {
      const next = checked
        ? [...new Set([...current.roles, code])]
        : current.roles.filter((role) => role !== code);
      return { ...current, roles: next.length > 0 ? next : current.roles };
    });
  }

  return (
    <section className="mx-auto max-w-3xl space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Button asChild variant="ghost" size="sm" className="mb-1 -ml-2">
            <Link to={USERS_ADMIN_PATH}>← Zurück zur Liste</Link>
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">
            <PersonChip
              className="text-2xl"
              firstName={user.firstName}
              lastName={user.lastName}
              displayName={user.displayName}
              profilePhotoUrl={user.profilePhotoUrl}
              avatarClassName="size-10"
            />
          </h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
          {user.ledTeams.length > 0 && (
            <p className="mt-1">
              <Badge variant="secondary" title={user.ledTeams.map((team) => team.name).join(', ')}>
                Teamleitung
              </Badge>
              <span className="ml-2 text-sm text-muted-foreground">
                {user.ledTeams.map((team) => team.name).join(', ')}
              </span>
            </p>
          )}
          {user.ssoOnly && (
            <p className="mt-1 text-sm text-muted-foreground">
              Anmeldung nur über Microsoft 365. Für dieses Konto gibt es kein lokales Passwort im
              Mitarbeiterportal.
            </p>
          )}
          {user.directoryLinkPending ? (
            <p className="mt-1 text-sm text-muted-foreground">
              Platzhalter für Microsoft 365. Der Verzeichnisabgleich trägt die Entra-ID ein. Bis
              dahin gibt es kein lokales Passwort.
            </p>
          ) : null}
        </div>
        <div className="flex flex-col items-start gap-1 sm:items-end">
          {user.isActive ? (
            <BadgeActive />
          ) : (
            <span className="text-sm font-medium text-destructive">Deaktiviert</span>
          )}
          {user.ssoOnly && (
            <span className="text-sm font-medium text-muted-foreground">Microsoft 365</span>
          )}
        </div>
      </div>

      {!user.ssoOnly && !user.directoryLinkPending && canWrite ? (
        <LocalPasswordCard userId={user.id} />
      ) : null}

      <form className="space-y-6" onSubmit={(event) => void onSave(event)}>
        <Card>
          <CardHeader>
            <CardTitle>Stammdaten</CardTitle>
            <CardDescription>Name, Stellenbezeichnung, Abteilung und Team.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field label="Vorname" htmlFor="firstName">
              <Input
                id="firstName"
                value={form.firstName}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, firstName: event.target.value })}
                required
              />
            </Field>
            <Field label="Nachname" htmlFor="lastName">
              <Input
                id="lastName"
                value={form.lastName}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, lastName: event.target.value })}
                required
              />
            </Field>
            <Field label="E-Mail" htmlFor="email">
              <Input id="email" value={user.email} disabled />
            </Field>
            <Field label={USER_JOB_TITLE_LABEL} htmlFor="jobTitle">
              <Input id="jobTitle" value={user.jobTitle ?? ''} disabled readOnly />
              <p className="text-xs text-muted-foreground">
                Wird von der Person unter Profil gepflegt.
              </p>
            </Field>
            <Field label="Personalnummer" htmlFor="personnelNumber">
              <Input
                id="personnelNumber"
                value={form.personnelNumber}
                disabled={!canWrite}
                maxLength={20}
                onChange={(event) => setForm({ ...form, personnelNumber: event.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Für DATEV und LOGA. Der Verzeichnis-Sync schreibt dieses Feld nicht.
              </p>
            </Field>
            <Field label="Telefon" htmlFor="phone">
              <Input
                id="phone"
                value={form.phone}
                disabled={!canWrite}
                onChange={(event) => setForm({ ...form, phone: event.target.value })}
              />
            </Field>
            <Field label="Abteilung" htmlFor="department">
              <Select
                value={form.departmentId}
                disabled={!canWrite}
                onValueChange={(value) => setForm({ ...form, departmentId: value })}
              >
                <SelectTrigger id="department" className="w-full">
                  <SelectValue placeholder="Keine Abteilung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Abteilung</SelectItem>
                  {departmentOptions.map((department) => (
                    <SelectItem key={department.id} value={department.id}>
                      {department.name}
                      {!department.isActive ? ' (archiviert)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {user.department && !user.department.isActive && (
                <p className="text-xs text-muted-foreground">
                  Zuordnung archiviert — bitte eine aktive Abteilung wählen.
                </p>
              )}
            </Field>
            <Field label="Vorgesetzte" htmlFor="manager">
              <Select
                value={form.managerId}
                disabled={!canWrite}
                onValueChange={(value) => setForm({ ...form, managerId: value })}
              >
                <SelectTrigger id="manager" className="w-full">
                  <SelectValue placeholder="Keine Vorgesetzte" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Vorgesetzte</SelectItem>
                  {managerOptions(people.data?.items ?? [], user).map((person) => (
                    <SelectItem key={person.id} value={person.id}>
                      {person.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <label className="flex items-start gap-2 pt-1 text-sm text-muted-foreground">
                <Checkbox
                  checked={form.managerLocked}
                  disabled={!canWrite}
                  onCheckedChange={(checked) =>
                    setForm({ ...form, managerLocked: checked === true })
                  }
                />
                <span>Verzeichnissync nicht überschreiben</span>
              </label>
            </Field>
            <Field label="Team" htmlFor="team">
              <Select
                value={form.teamId}
                disabled={!canWrite}
                onValueChange={(value) => setForm({ ...form, teamId: value })}
              >
                <SelectTrigger id="team" className="w-full">
                  <SelectValue placeholder="Kein Team" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Kein Team</SelectItem>
                  {teamOptions.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                      {!team.isActive ? ' (archiviert)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {user.team && !user.team.isActive && (
                <p className="text-xs text-muted-foreground">
                  Zuordnung archiviert — bitte ein aktives Team wählen.
                </p>
              )}
            </Field>
            <p className="text-sm text-muted-foreground sm:col-span-2">
              Letzte Anmeldung: {formatDateTime(user.lastLoginAt)} · 2FA:{' '}
              {user.totpEnabled ? 'aktiviert' : 'nicht aktiv'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Rollen und Berechtigungsgruppen</CardTitle>
            <CardDescription>
              {canAssignRoles
                ? 'Berechtigungen ergeben sich aus den zugewiesenen Rollen.'
                : 'Rollen darf nur ein IT-Admin ändern.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            {(lookups.data?.roles ?? []).map((role) => (
              <label
                key={role.id}
                className="flex items-start gap-3 rounded-xl border border-border bg-card p-3 text-sm shadow-sm"
              >
                <Checkbox
                  checked={form.roles.includes(role.code)}
                  disabled={
                    !canAssignRoles ||
                    (role.code === RoleCode.PLATFORM_OPERATOR &&
                      !canAssignOperator &&
                      !form.roles.includes(role.code))
                  }
                  onCheckedChange={(checked) => toggleRole(role.code, checked === true)}
                />
                <span>
                  <span className="font-medium">{role.name}</span>
                  <span className="block text-xs text-muted-foreground">
                    {role.isSystem ? 'Systemrolle' : 'Eigene Gruppe'}
                  </span>
                </span>
              </label>
            ))}
          </CardContent>
        </Card>

        {canWrite && (
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
            <Button type="submit" disabled={updateUser.isPending || assignRoles.isPending}>
              {(updateUser.isPending || assignRoles.isPending) && (
                <Loader2Icon className="animate-spin" aria-hidden />
              )}
              Speichern
            </Button>
            {user.isActive ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive" disabled={isSelf}>
                    Benutzer deaktivieren
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Benutzer deaktivieren?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Das Konto wird nicht gelöscht. Die Person kann sich nicht mehr anmelden, die
                      Audit-Historie bleibt erhalten.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                    <AlertDialogAction
                      variant="destructive"
                      onClick={() => void onSetActive(false)}
                    >
                      Deaktivieren
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <Button type="button" variant="outline" onClick={() => void onSetActive(true)}>
                Benutzer reaktivieren
              </Button>
            )}
          </div>
        )}
        {isSelf && canWrite && (
          <p className="text-sm text-muted-foreground">
            {copy('platform.account.self.deactivate.order')}
          </p>
        )}
      </form>
    </section>
  );
}

function managerOptions(
  people: { id: string; displayName: string }[],
  user: UserDetail,
): { id: string; displayName: string }[] {
  const options = people.filter((person) => person.id !== user.id);
  if (user.manager && !options.some((person) => person.id === user.manager?.id)) {
    options.unshift(user.manager);
  }
  return options;
}

function LocalPasswordCard({ userId }: { userId: string }) {
  const copy = useVoiceCopy();
  const setPassword = useSetLocalPassword(userId);
  const sendLink = useSendLocalPasswordLink(userId);
  const [password, setPasswordValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSet(event: FormEvent) {
    event.preventDefault();
    const parsed = passwordSchema.safeParse(password);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Das Passwort ist ungültig.');
      return;
    }
    setError(null);
    try {
      await setPassword.mutateAsync({ password: parsed.data });
      setPasswordValue('');
      toast.success('Passwort gespeichert.');
    } catch (caught) {
      toast.error(
        caught instanceof ApiRequestError ? caught.message : 'Passwort nicht gespeichert.',
      );
    }
  }

  async function onLink() {
    try {
      await sendLink.mutateAsync();
      toast.success('Link zum Setzen des Passworts wurde gesendet.');
    } catch (caught) {
      toast.error(
        caught instanceof ApiRequestError ? caught.message : 'Der Link wurde nicht gesendet.',
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lokales Passwort</CardTitle>
        <CardDescription>{copy('platform.profile.password.local')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form className="space-y-3" onSubmit={(event) => void onSet(event)}>
          <Field label="Neues Passwort" htmlFor="local-reset-password">
            <Input
              id="local-reset-password"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPasswordValue(event.target.value)}
            />
          </Field>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={setPassword.isPending || password.length === 0}>
              Passwort setzen
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={sendLink.isPending}
              onClick={() => void onLink()}
            >
              Link senden
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
    </div>
  );
}

function BadgeActive() {
  return <span className="text-sm font-medium text-emerald-700">Aktiv</span>;
}
